// ============================================================================
// CHANGO V5.0 — Ops Audit Log
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';

const SOURCE_LABEL = {
  ops_action_log: '平台操作',
  ops_audit_log: '工作区审计',
};

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', { hour12: false });
}

function compactJson(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  try { return JSON.stringify(value); } catch { return String(value); }
}

function SourcePill({ source }) {
  const cls = source === 'ops_audit_log'
    ? 'bg-indigo-100 text-indigo-700'
    : 'bg-slate-200 text-slate-700';
  return <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>{SOURCE_LABEL[source] || source}</span>;
}

export default function OpsAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '100');
    if (source) params.set('source', source);
    if (q.trim()) params.set('q', q.trim());
    if (workspaceId.trim()) params.set('workspace_id', workspaceId.trim());
    return params.toString();
  }, [source, q, workspaceId]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get(`/api/ops/audit-log?${queryString}`);
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      const msg = err instanceof HttpError ? `${err.code}: ${err.message}` : '加载操作日志失败';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <Link to="/ops/dashboard" className="text-xs text-slate-500 hover:text-slate-700">← 返回控制台</Link>
            <h1 className="mt-1 text-xl font-semibold">操作审计日志</h1>
            <p className="mt-1 text-sm text-slate-500">平台操作与工作区审核留痕，只读查看。</p>
          </div>
          <button
            type="button"
            onClick={load}
            className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100"
          >
            刷新
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6">
        <section className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">日志来源</span>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">全部</option>
              <option value="ops_action_log">平台操作</option>
              <option value="ops_audit_log">工作区审计</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-slate-500">工作区 ID</span>
            <input
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value.replace(/[^\d]/g, ''))}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="例如 2"
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium text-slate-500">关键词</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="动作、邮箱、公司名、备注"
            />
          </label>
        </section>

        {error ? <div role="alert" className="mb-4 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}

        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div className="text-sm text-slate-600">
              {loading ? '加载中...' : `共 ${total} 条，当前显示 ${items.length} 条`}
            </div>
          </div>

          {items.length === 0 && !loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">暂无操作日志</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">时间</th>
                    <th className="px-4 py-3">来源</th>
                    <th className="px-4 py-3">动作</th>
                    <th className="px-4 py-3">操作人</th>
                    <th className="px-4 py-3">工作区</th>
                    <th className="px-4 py-3">备注 / Payload</th>
                    <th className="px-4 py-3">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((row) => {
                    const detail = row.note || compactJson(row.payload) || compactJson(row.after_state) || compactJson(row.before_state) || '—';
                    return (
                      <tr key={`${row.source}-${row.id}`} className="align-top hover:bg-slate-50">
                        <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDate(row.created_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3"><SourcePill source={row.source} /></td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-800">{row.action_type}</td>
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-slate-700">#{row.actor_id ?? '—'}</div>
                          <div className="text-xs text-slate-500">{row.actor_email || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          {row.workspace_id ? (
                            <>
                              <div className="font-mono text-xs">#{row.workspace_id}</div>
                              <div className="text-xs text-slate-500">{row.workspace_company_name || '—'}</div>
                            </>
                          ) : '—'}
                        </td>
                        <td className="max-w-xl px-4 py-3">
                          <code className="block whitespace-pre-wrap break-words rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">
                            {detail}
                          </code>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500">{row.source_ip || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
